
import Vue from "vue";
import * as types from '../mutation-types'
import features, {subscriptionFeaturesDefaults} from "@/Features";
import {hasValue, trueIfUndef} from "@/utilities/utils";
import {configCatUser, getFeatureConfig} from "@/utilities/ConfigCat";
import {powerups} from "@/Constants";
import {StringUtils as $strings} from "@/utilities/Strings";
import {beginDownloadingAccount} from "@/store/actions";

function api() {
    return Vue.prototype.$api
}

const state = {
    accountsBeingLoaded : [],
    subscriptions : [],
    lastFetchedAt : false,
    overviews : [],
    currentAccountOverview: false,
    accountToLoadAfterHootsuiteAuthorization : false,
    accountToLoadAfterOauth:{
        sniply: false
    },
    accountIntegrationData: {},
    accountFeatureData: {},
    accountData: {},
    readyForLogin: false,
}

export const featuresForAccount = (subscriptions, codeFeatures, currentAccountFeatureData) => account => {
    let subFeatures = subscriptionFeaturesForAccount(subscriptions)(account);
    let globFeatures = codeFeatures || features;
    let featureToggles = currentAccountFeatureData;
    let keys = [...new Set([...Object.keys(globFeatures), ...Object.keys(subFeatures)])];
    let featureStatus = key => {
        let codeToggle = trueIfUndef(globFeatures[key]);
        let toggle = featureToggles[key];
        let toggledOn = hasValue(toggle) ? toggle : codeToggle;
        let hasSubscription = trueIfUndef(subFeatures[key]);
        return hasSubscription && toggledOn;
    }
    return keys.reduce((result, key) => {
        return {
            ...result,
            [key]: featureStatus(key)
        }
    }, {})
};

const currentSubscription = state => subscriptionForAccount(state.subscriptions, state.currentAccountOverview)
const subscriptionForAccount = (subscriptions, currentAccount) => {
    subscriptions = subscriptions.filter(s => currentAccount && s.account_id === currentAccount.id)
    if (subscriptions.length){
        return subscriptions[0];
    }
}

const currentPlans = state => (currentSubscription(state)||{}).plan_items||[];

const oneIfNotDef = x => !isNaN(parseInt(x)) ? parseInt(x) : 1

const planMetaValuesReducer = reduceValues => (combinedMetaData, plan) => (
    Object.keys(combinedMetaData).reduce((meta, key) => ({
        ...meta,
        [key]: reduceValues(combinedMetaData[key], plan[key], oneIfNotDef(plan.quantity))
    }), {}));

const reducedPlanMetaValues = (plans, reduceValues, initialMeta) =>
    plans.reduce(planMetaValuesReducer(reduceValues), initialMeta);

const subscriptionFeatures = state => _subscriptionFeatures(currentPlans(state));

const _subscriptionFeatures = plans => reducedPlanMetaValues(plans,
    (result, planVal) => (result || planVal) ? true : ((result===false)||(planVal===false)) ? false : undefined,
    subscriptionFeaturesDefaults
);

const subscriptionFeaturesForAccount = subscriptions => account => _subscriptionFeatures((subscriptionForAccount(subscriptions, account)||{}).plan_items||[])

const reducePlanLimits = plans => reducedPlanMetaValues(plans, (result, planVal, quantity) =>
        result === 'unlimited' || planVal === 'unlimited' ? 'unlimited' : Number(result) + Number(planVal) * quantity,
    { topics: 0, collections: 0, queries: 0, users: 0, contentPolicyRules:0, gallery_sites: 0, shutterstock_images: 0, proofpoint_configs: 0, contentTemplateCount: 0 }
)

const subscriptionLimits = state => {
    let plans = currentPlans(state);
    return plans.length ? reducePlanLimits(plans) :{ topics: 1, collections: 0, queries: 15, users: 1, contentPolicyRules:0 }
};


let subscriptionDataForAccount = function (allSubscriptions, acc) {
    let subscriptions = allSubscriptions.filter(sub => sub.account_id === acc.id);
    let planItems = subscriptions.flatMap(x => x.plan_items);
    let features = reducedPlanMetaValues(planItems,
        (result, planVal) => !!(result || planVal),
        {rss: false, crossAccount: false}
    );

    let limits = planItems.length ? reducePlanLimits(planItems) : {topics: 1, collections: 0, queries: 15, users: 1, contentPolicyRules:0}
    return {
        accountId: acc.id,
        subscriptions, planItems, features, limits
    }
};

const expect404s = e => {
    if (e.response.status !== 404)
        throw e;
};

const fetchAccountSummary = async(accountUrl, cancelToken) => { //deprecated in favor of /overview, which has all info and more
    console.log('* fetchAccountSummary accountUrl', accountUrl)
    return await api().limit.get(accountUrl, {cancelToken});
};

const fetchAllAccountsForUser = async() => {
    return await api().limit.get('/accounts');
};

const fetchAccountUsers = async(accountId, cancelToken) => {
    return await api().limit.get(`/accounts/${accountId}/users`, {cancelToken, ignoreCache: true});
};

const fetchAccountSubscription = async(accountId) => {
    try {
        return await api().limit.get(`/accounts/${accountId}/subscription`);
    } catch(e) {
        expect404s(e)
    }
};

const fetchAccountIntegrations = async() => {
    return await api().limit.get('/integrations?meta=true');
};

const fetchAccountPowerupSettings = async(accountId, powerup, cancelToken) => {
    //console.log('* fetchAccountPowerupSettings accountId, powerup', accountId, powerup)
    try {
        return await api().limit.get(`/accounts/${accountId}/${powerup}-settings`, {cancelToken, ignoreCache: true});
    } catch (e) {
        console.error(`fetchAccountPowerupSettings error: ${e}`)
    }
};

const fetchAccountNotes = async(accountId, cancelToken) => {
    return await api().limit.get(`/accounts/${accountId}/notes`, {cancelToken, ignoreCache: true});
};

const fetchAccountTopics = async(accountId, cancelToken) => {
    return await api().limit.get(`/accounts/${accountId}/active-topics`, {cancelToken, ignoreCache: true});
};

const fetchAccountFolders = async(accountId, cancelToken) => {
    return await api().limit.get(`/accounts/${accountId}/folders`, {cancelToken, ignoreCache: true});
};

const fetchAccountCollections = async(accountId, cancelToken) => {
    return await api().limit.get(`/accounts/${accountId}/collections`, {cancelToken, ignoreCache: true});
};

const fetchAccountOverview = async(accountId, cancelToken) => {
    return await api().get(`/accounts/${accountId}/overview`, {cancelToken, ignoreCache: true});
};

const getCurrentAccount = (summaries, user) => {
    console.log('* accounts.js getCurrentAccount summaries', summaries)
    let userPrimary = user && user.default_account;
    let deviceDefault = (JSON.parse(localStorage.getItem('defaultAccounts') || '{}') || {})[user && user.id];
    let defaultAccount = deviceDefault || userPrimary;
    const summariesDerivedDefaultAccount = summaries.filter(s => s.id === defaultAccount.slice(-36))[-1];
    console.log('* accounts.js getCurrentAccount summariesDerivedDefaultAccount', summariesDerivedDefaultAccount)

    console.log('* accounts.js getCurrentAccount localStorage.getItem(\'accountId\')', localStorage.getItem('accountId')) // null for creating new account via signup
    const recentAccountId = localStorage.getItem('accountId') || {};
    const summariesDerivedRecentAccount = summaries.filter(s => s.id === recentAccountId)[0];
    console.log('* accounts.js getCurrentAccount summariesDerivedRecentAccount', summariesDerivedRecentAccount)

    if(summariesDerivedRecentAccount) {
        console.log('* accounts.js getCurrentAccount if summariesDerivedRecentAccount', summariesDerivedRecentAccount)
        return summariesDerivedRecentAccount;
    }
    else if(summariesDerivedDefaultAccount) {
        console.log('* accounts.js getCurrentAccount if summariesDerivedDefaultAccount', summariesDerivedDefaultAccount)
        return summariesDerivedDefaultAccount;
    } else {
        console.warn('getCurrentAccount: Error confirming default user account matching current account, default to latest account');
        return summaries.at(-1);
    }
};


const getters = {
    shareProviderForCurrentAccount: (state, getters, rootState, rootGetters) => {
        let currentAccount = getters.currentAccountOverview;
        let shareProviderName = currentAccount && currentAccount.share_provider;
        let shareProviderOption = rootGetters.shareProviders.find(sp => sp.name === shareProviderName);
        if (shareProviderOption) return shareProviderName;


        shareProviderName = rootGetters.vendor;
        shareProviderOption = rootGetters.shareProviders.find(sp => sp.name === shareProviderName);
        if (shareProviderOption) return shareProviderName;

        return 'buffer'
    },
    currentShareProviderOptions: state => {
        return state.currentAccountOverview
            && state.currentAccountOverview.id
            && state.accountIntegrationData[state.currentAccountOverview.id];
    },
    currentSubscription, currentPlans, subscriptionFeatures, subscriptionLimits, subscriptionFeaturesForAccount,
    currentPackage: (s, g) => g.currentPlans.find(p => p.plan_key === g.currentSubscription.plan_key),
    currentAccountFeatureData: (s) => s.accountFeatureData[s.currentAccountOverview && s.currentAccountOverview.id]||{},
    accountFeatures: (state, getters, rootState, rootGetters) => {
        return featuresForAccount(state.subscriptions, rootGetters.features, getters.currentAccountFeatureData)(getters.currentAccountOverview)
    },
    allAccounts : state => {
        return state.overviews.filter(x => x)
    },
    accountsBeingLoaded : state => state.accountsBeingLoaded,
    subscriptions : state => state.subscriptions,
    accountsLastFetchedAt : state => state.lastFetchedAt,
    subscriptionPlan : state => {
        let subscriptions = state.subscriptions.filter(s => state.currentAccountOverview && s.account_id === state.currentAccountOverview.id)
        if (subscriptions.length){
            return subscriptions[0].package;
        }
    },
    subscriptionDataByAccount: state => account => subscriptionDataForAccount(state.subscriptions, account),
    currentAccountOverview : state => state.currentAccountOverview,
    currentAccountData: (state, getters) => state.accountData[getters.currentAccountOverview && getters.currentAccountOverview.id]||{},
    currentAccountDataOpenai: (state, getters) => getters.currentAccountData.openai,
    accountsLoadedForUser : state => !!state.lastFetchedAt,
    accountNotes : state => accountId => state.accountData[accountId] && state.accountData[accountId].notes,
    readyForLogin: state => state.readyForLogin,
    accountIntegrationData: state => accountId => state.accountIntegrationData[accountId],
    externalApis : (state, getters) => {
        let currentAccountData = state.accountIntegrationData[getters.currentAccountOverview && getters.currentAccountOverview.id] || {}
        let otherKeys = Object.keys(state.accountIntegrationData)
            .filter(accId => accId !== (getters.currentAccountOverview && getters.currentAccountOverview.id))
        let otherAccountData = otherKeys.reduce((data, accId) => ({...data, [accId]: state.accountIntegrationData[accId]}), {})
        return {...currentAccountData, ...otherAccountData}
    },
    isLoaded : state => accountId => !!state.overviews.find(acc => acc.id === accountId),
    accountById: state => accountId => state.overviews.find(acc => acc.id === accountId),
    isLoadingAccount : state => accountId => !!state.accountsBeingLoaded.find(id => id === accountId),
    isLoadingDataForAnyAccount : state => state.accountsBeingLoaded.length > 0,
    accountToLoadAfterHootsuiteAuthorization : state => state.accountToLoadAfterHootsuiteAuthorization,
    accountToLoadAfterOauth: state => provider => state.accountToLoadAfterOauth[provider]
}

const actions = {
    async changeDataForAccount({commit, dispatch, getters, rootState}, {overview}) {
        console.log('* accounts.js changeDataForAccount overview', overview)
        await commit("ACCOUNT_OVERVIEW_DID_CHANGE", {overview});
        await dispatch("loadingAccountStarted", {id: overview.id});
        await commit("ACCOUNT_OVERVIEW_DID_LOAD", {overview});
        localStorage.setItem('accountId', overview.id);
        localStorage.setItem('accountId-iat', (new Date()).toISOString())

        const currentCollectionNotPartOfOverviewCollections = !getters.currentAccountOverview?.collections?.map(c => c.id).includes(getters.currentCollection?.id);
        const currentAccountOverviewHasCollections = getters.currentAccountOverview?.collections?.length > 0;

        // FIX: Only reset currentCollection if it truly doesn't exist OR if it's not in the current account's collections
        // Don't reset if currentCollection is stored but hasn't been resolved yet (when collections module hasn't loaded)
        const hasStoredCollectionId = rootState.collections && rootState.collections.currentCollection && state.collections.currentCollection !== false;
        const collectionIsResolved = getters.currentCollection !== null && getters.currentCollection !== undefined;

        // Only reset if:
        // 1. There's no stored collection ID at all, OR
        // 2. The collection IS resolved (found in rootState.collections array) but doesn't belong to current account
        const shouldResetCollection = currentAccountOverviewHasCollections &&
            (!hasStoredCollectionId || (collectionIsResolved && currentCollectionNotPartOfOverviewCollections));

        console.log('* accounts.js changeDataForAccount shouldResetCollection', shouldResetCollection)
        console.log('* accounts.js changeDataForAccount hasStoredCollectionId', hasStoredCollectionId)
        console.log('* accounts.js changeDataForAccount collectionIsResolved', collectionIsResolved)
        console.log('* accounts.js changeDataForAccount currentCollectionNotPartOfOverviewCollections', currentCollectionNotPartOfOverviewCollections)

        if(shouldResetCollection) {
            const collectionToSet = getters.currentAccountOverview.collections[0];
            console.log('* accounts.js changeDataForAccount setting collection to first in account', collectionToSet)
            await dispatch('setCurrentCollection', { collection : collectionToSet });
        }

        await dispatch("loadingAccountFinished", {id: overview.id});
    },
    async loadAccountSubscription({commit}, {account}){
        let subResp = await fetchAccountSubscription(account.id);
        let subscription = subResp && subResp.data;
        if (subscription) await commit('SET_SUBSCRIPTION', {subscription});
    },
    async loadAccountData({commit, getters}, {account}){
        //Temp guard against a bug with undefined accounts coming in to prevent app crashes. Figure out bug later
        if(!account){console.error('cannot loadAccountData for undefined account'); return;}

        console.log('* accounts.js loadAccountData account', account)
        let subResp = await fetchAccountSubscription(account.id);
        let subscription = subResp && subResp.data;
        if (subscription) await commit('SET_SUBSCRIPTION', {subscription});

        let p_powerups = [
            ...powerups
        ].map(async(powerup) => {
            let data = await fetchAccountPowerupSettings(account.id, powerup);
            let dataOrFalse = (data && data.data && Object.keys(data.data).length > 0 && data.data) || false;
            return {powerup, data: dataOrFalse};
        })

        let allPowerupData = await Promise.all(p_powerups);
        allPowerupData.forEach(({powerup, data}) => {
            let token = data && data.access_token;
            commit('SET_POWERUP_DATA_FOR_ACCOUNT', {data : token ? {token, ...data} : false, account, powerup});
        })

        if (getters.accountFeatures && getters.accountFeatures.accountNotes){
            let notesResp = await fetchAccountNotes(account.id);
            let notes = notesResp && notesResp.data;
            if (notes) await commit('SET_ACCOUNT_DATA', {account, key: 'notes', data: notes});
        }
    },
    async setPowerupDataForAccount({commit}, {data, account, powerup}) {
        commit('SET_POWERUP_DATA_FOR_ACCOUNT', {data, account, powerup})
    },
    async loadUserAccounts({commit, dispatch}, {}){
        let resp = await fetchAllAccountsForUser();
        let accounts = resp.data;
        commit('SET_ACCOUNT_LOADED_DATE', {date: new Date()});
        accounts.forEach(account => {
            commit('ACCOUNT_OVERVIEW_DID_LOAD', {overview: account});
        })
    },
    async loadAccountUsers({commit}, {account}){
        let resp = await fetchAccountUsers(account.id);
        let teamMembers = resp.data;
        await commit('SET_TEAM_MEMBERS', {account, teamMembers});
    },
    async loadingAccountStarted({commit}, {id}){
        commit('LOADING_ACCOUNT_STARTED', {id});
    },
    async loadingAccountFinished({commit}, {id}){
        commit('LOADING_ACCOUNT_FINISHED', {id});
    },
    async setAccountToLoadAfterHootsuiteAuthorization({commit}, {account}){
        await commit('SET_ACCOUNT_TO_LOAD_AFTER_HOOTSUITE_AUTHORIZATION', {account});
    },
    async setAccountToLoadAfterOauth({commit}, {account, provider}){
        await commit('SET_ACCOUNT_TO_LOAD_AFTER_OAUTH', {account, provider});
    },
    async setShareProvider({commit, getters}, {account, shareProvider}) {
        await commit('SET_SHARE_PROVIDER_FOR_ACCOUNT', {account, shareProvider});
    },
    async setReadyForLogin({commit}, val) {
        await commit('SET_READY_FOR_LOGIN', val);
    }
}

const mutations = {
    SET_READY_FOR_LOGIN(state, val) {
        state.readyForLogin = val;
    },
    SET_SHARE_PROVIDER_FOR_ACCOUNT(state, {account, shareProvider}) {
        let existing = state.overviews.find(acc => acc.id === account.id);
        let index = state.overviews.indexOf(existing);
        let replacement = {...existing, share_provider: shareProvider};
        state.overviews.splice(index, 1, replacement);
        state.currentAccountOverview = replacement;
    },
    SET_ACCOUNT_TO_LOAD_AFTER_OAUTH(state, {account, provider}) {
        state.accountToLoadAfterOauth = {...state.accountToLoadAfterOauth, [provider]: account};
    },
    SET_ACCOUNT_TO_LOAD_AFTER_HOOTSUITE_AUTHORIZATION(state, {account}) {
        state.accountToLoadAfterHootsuiteAuthorization = account;
    },
    LOADING_ACCOUNT_STARTED(state, {id}){
        state.accountsBeingLoaded.push(id);
    },
    LOADING_ACCOUNT_FINISHED(state, {id}){
        state.accountsBeingLoaded = state.accountsBeingLoaded.filter(accountId => accountId !== id);
    },
    SET_ACCOUNT_LOADED_DATE(state, {date}){
        state.lastFetchedAt = date;
    },
    SET_SUBSCRIPTION(state, {subscription}){
        let exists = state.subscriptions.find(s => s.account_id === subscription.account_id);
        if (exists){
            let index = state.subscriptions.indexOf(exists);
            state.subscriptions.splice(index, 1, subscription);
        }
        else state.subscriptions.push(subscription);
    },
    ACCOUNT_OVERVIEW_DID_LOAD(state, {overview}){
        let exists = state.overviews.find(s => s.id === overview.id);
        if (exists){
            let index = state.overviews.indexOf(exists);
            state.overviews.splice(index, 1, overview);
        }
        else state.overviews.push(overview);
        if (overview.id === (state.currentAccountOverview && state.currentAccountOverview.id))
            state.currentAccountOverview = overview;
    },
    ACCOUNT_OVERVIEW_DID_CHANGE(state, {overview}){
        state.currentAccountOverview = overview;
    },
    SET_POWERUP_DATA_FOR_ACCOUNT(state, {data, account, powerup}){
        let accountData = state.accountIntegrationData[account.id] || {};
        let powerupData = accountData[powerup] || {};
        state.accountIntegrationData = {
            ...state.accountIntegrationData,
            [account.id]: {
                ...accountData,
                [powerup]: data
            }
        };
    },
    SET_ACCOUNT_FEATURE_DATA(state, {account, key, data}){
        let accountData = state.accountFeatureData[account.id] || {};
        state.accountFeatureData = {
            ...state.accountFeatureData,
            [account.id]: {
                ...accountData,
                [key]: data
            }
        };
    },
    SET_ACCOUNT_DATA(state, {account, key, data}){
        let accountData = state.accountData[account.id] || {};
        state.accountData = {
            ...state.accountData,
            [account.id]: {
                ...accountData,
                [key]: data
            }
        };
    },
    [types.USER_DID_LOG_OUT](state){
        state.accountsBeingLoaded = [];
        state.subscriptions = [];
        state.lastFetchedAt = false;
        state.overviews = [];
        state.currentAccountOverview = false;
        state.accountToLoadAfterHootsuiteAuthorization = false;
        state.accountToLoadAfterOauth = {
            sniply: false
        };
        state.accountIntegrationData = {};
        state.accountFeatureData = {};
        state.accountData = {};
        state.readyForLogin = false;
    },
}

export default {
    state,
    getters,
    actions,
    mutations
}
